import { Phone } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import QuickContact from "@/components/QuickContact";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import { ReactNode } from "react";
import { useLanguage } from "@/hooks/useLanguage";
import SEO from "@/components/SEO";
import { useLocation } from "react-router-dom";
import { getComprehensiveSEOData } from "@/lib/comprehensive-seo-data";
import { RelatedLinks } from "@/components/RelatedLinks";
import AIReadabilityBlock from "@/components/AIReadabilityBlock";

interface FAQItem {
  question: string;
  answer: string;
}

interface GenericPracticePageProps {
  title: string;
  subtitle: string;
  icon: ReactNode;
  mainContent: ReactNode;
  heroImage?: string;
  category?: "immigration" | "criminal" | "family" | "personalInjury" | "realEstate" | "business" | "probate" | "tax";
  /** Key points for AI readability block - enables AI search engine optimization */
  aiKeyPoints?: string[];
  /** FAQs for AI readability block - adds FAQ schema markup */
  aiFaqs?: FAQItem[];
  /** Service description for AI schema - defaults to subtitle if not provided */
  aiServiceDescription?: string;
}

const GenericPracticePage = ({ 
  title, 
  subtitle, 
  icon, 
  mainContent, 
  heroImage, 
  category,
  aiKeyPoints,
  aiFaqs,
  aiServiceDescription
}: GenericPracticePageProps) => {
  const { language } = useLanguage();
  const location = useLocation();
  const seoData = getComprehensiveSEOData(location.pathname, language);
  const displayH1 = seoData.h1;
  
  const translations = {
    scheduleConsultation: {
      en: "Schedule Consultation",
      es: "Agende Consulta",
      vi: "Đặt Lịch Tư Vấn"
    },
    call: {
      en: "Call:",
      es: "Llamar:",
      vi: "Gọi:"
    },
    needHelp: {
      en: `Need Help with ${title}?`,
      es: `¿Necesita Ayuda con ${title}?`,
      vi: `Cần Trợ Giúp Về ${title}?`
    },
    contactDesc: {
      en: "Contact us today for experienced legal representation.",
      es: "Contáctenos hoy para representación legal experimentada.",
      vi: "Liên hệ với chúng tôi ngay hôm nay để được đại diện pháp lý có kinh nghiệm."
    }
  };

  // Show AI Readability Block if key points or FAQs are provided
  const showAIBlock = (aiKeyPoints && aiKeyPoints.length > 0) || (aiFaqs && aiFaqs.length > 0);

  return (
    <div className="min-h-screen">
      <SEO />
      <Header />

      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        {heroImage && (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${heroImage})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/90 to-primary-dark/95" />
          </div>
        )}
        {!heroImage && (
          <div className="absolute inset-0 bg-gradient-hero" />
        )}
        <div className="container mx-auto px-4 relative z-10 text-center text-primary-foreground">
          {icon}
          <h1 className="text-5xl md:text-6xl font-bold mb-6">{displayH1}</h1>
          <p className="text-xl mb-8 max-w-3xl mx-auto opacity-95">
            {subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="hero">
              <LocalizedLink to="/appointment">{translations.scheduleConsultation[language]}</LocalizedLink>
            </Button>
            <a href="tel:8329303059">
              <Button size="lg" variant="outline" className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
                <Phone className="mr-2 h-5 w-5" />
                {translations.call[language]} (832) 930-3059
              </Button>
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {mainContent}
          </div>
        </div>
      </section>

      {/* AI Readability Block for search engine optimization */}
      {showAIBlock && (
        <AIReadabilityBlock
          serviceName={title}
          serviceDescription={aiServiceDescription || subtitle}
          keyPoints={aiKeyPoints}
          faqs={aiFaqs}
        />
      )}

      {category && <RelatedLinks category={category} />}

      <QuickContact />

      <CTASection 
        title={translations.needHelp[language]}
        description={translations.contactDesc[language]}
      />
      <Footer />
    </div>
  );
};

export default GenericPracticePage;
